import type { InstallableModel, VoiceModelAsset } from '../types/LocalAiTypes';

export const LOCAL_AI_CACHE = 'varza-local-ai-models-v1';
let fetchCacheInstalled = false;

export async function isAssetCached(asset: VoiceModelAsset): Promise<boolean> {
  if (!('caches' in globalThis)) return false;
  const cache = await caches.open(LOCAL_AI_CACHE);
  const response = await cache.match(asset.url);
  return response?.ok === true && response.headers.get('X-Content-SHA256') === asset.sha256;
}

export async function isModelCached(model: InstallableModel): Promise<boolean> {
  const statuses = await Promise.all(model.assets.map(isAssetCached));
  return statuses.every(Boolean);
}

export async function clearLocalModelCache(): Promise<void> {
  if ('caches' in globalThis) await caches.delete(LOCAL_AI_CACHE);
}

export async function cachedModelBytes(models: readonly InstallableModel[]): Promise<number> {
  const statuses = await Promise.all(models.flatMap((model) => model.assets.map(async (asset) => ({
    asset,
    cached: await isAssetCached(asset),
  }))));
  return statuses.reduce((total, item) => total + (item.cached ? item.asset.bytes : 0), 0);
}

export async function installModelFetchCache(): Promise<void> {
  if (!('caches' in globalThis) || fetchCacheInstalled) return;
  fetchCacheInstalled = true;
  const nativeFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const request = new Request(input, init);
    const url = new URL(request.url, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin || !url.pathname.startsWith('/api/v1/voice/models/')) {
      return nativeFetch(input, init);
    }
    const cache = await caches.open(LOCAL_AI_CACHE);
    const cached = await cache.match(request);
    if (cached) return cached;
    const response = await nativeFetch(request);
    /*
     * Only a whole, successful response may be stored, and a failed store must never fail the
     * request it was serving.
     *
     * Both halves of that were learned the hard way. Transformers.js finds out whether a file
     * exists by asking for one byte of it — `Range: bytes=0-0` — and the model endpoint honours
     * ranges, so the answer is a 206. `Response.ok` is true for a 206, but `Cache.put` rejects
     * outright on any partial response, and that rejection came back out of this wrapper as a
     * failed fetch. The library reads a failed probe as "the file is not there", so
     * `preprocessor_config.json` looked absent, the pipeline was built with a null processor,
     * and the first transcription died on `processor.feature_extractor` — a null-property error
     * three layers away from anything to do with caching.
     */
    if (response.status === 200) {
      try {
        await cache.put(request, response.clone());
      } catch {
        // A full disk, a private window, or storage switched off. None of them are reasons to
        // fail a request that already has its answer.
      }
    }
    return response;
  };
}
